import React, { useEffect, useRef, useState } from 'react';
import {
  createC2PSession,
  initPayClickToPay,
  confirmClickToPay
} from '../../../api';
import { buildPaymentBag } from '../../../actions/buildPaymentBag';
import { toastifyMessage } from '../../../handlers/toastifyMessage';
import './ClickToPayCheckout.css';

const loadScript = (src) =>
  new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }
    const sc = document.createElement('script');
    sc.src = src;
    sc.async = false;
    sc.onload = () => resolve();
    sc.onerror = () => reject(new Error('Failed to load ' + src));
    document.body.appendChild(sc);
  });

const show = (id) => {
  const el = document.getElementById(id);
  if (el) el.style.display = '';
};
const hide = (id) => {
  const el = document.getElementById(id);
  if (el) el.style.display = 'none';
};

const maskEmail = (email) => {
  if (!email) return '';
  const [local, domain] = email.split('@');
  if (!domain) return email;
  if (local.length <= 2) return '*'.repeat(local.length) + '@' + domain;
  return local[0] + '*****' + local[local.length - 1] + '@' + domain;
};

const isElementVisible = (el) =>
  el &&
  el.offsetParent !== null &&
  getComputedStyle(el).display !== 'none' &&
  getComputedStyle(el).visibility !== 'hidden';

// Mirrors the backend registration total (init_pay_all / start3 loop) so the
// Click to Pay SDK can display the amount before the cart is registered. This
// value is DISPLAY ONLY — init_pay_web recomputes tot_amount server-side and
// that is what gets charged.
const computeBagTotal = (bag) =>
  bag.reduce((sum, it) => {
    const qty = parseInt(it.quantity, 10) > 0 ? parseInt(it.quantity, 10) : 1;
    let months = parseInt(it.month_count, 10);
    if (!months || it.recuring === 'Y') months = 1;
    const amt = parseFloat(it.amount) || 0;
    const t = String(it.item_type);
    return sum + (t === '1' || t === '5' ? amt * months * qty : amt * qty);
  }, 0);

const checkC2PConsent = () => {
  for (const containerId of [
    'consent-containerMaster',
    'consent-containerVisa'
  ]) {
    const container = document.getElementById(containerId);
    const consent = container && container.querySelector('src-consent');
    if (consent && consent.shadowRoot) {
      const checkbox = consent.shadowRoot.querySelector(
        'input[name="consent-checkbox"]'
      );
      if (isElementVisible(container) && checkbox && checkbox.checked)
        return true;
    }
  }
  return false;
};

const ClickToPayCheckout = ({ cart, onSuccess, onClose }) => {
  const [phase, setPhase] = useState('loading'); // loading | ready | paying | done | error
  const [errorMsg, setErrorMsg] = useState('');
  // NOTE: the email field is intentionally UNCONTROLLED. MPGS session.js injects
  // secure <iframe> hosted fields onto the readonly card inputs after
  // PaymentSession.configure. Those iframes are DOM that React does not own, so
  // any re-render of this component reconciles them away and the fields go dead
  // (this is why the CVV was not typable). Keeping email uncontrolled means zero
  // re-renders between configure and card entry, so the hosted fields survive.
  const sess = useRef({ mco: '', amount: '', sessionId: '' });
  // The built payment bag, kept so confirmPay can send it to init_pay_web —
  // registration/mco minting now happens at charge time, not at modal open.
  const bagRef = useRef([]);
  const flags = useRef({
    inited: false,
    doEnroll: false,
    emailLookupInitiated: false,
    addingNewCard: false,
    settled: false,
    psConfigured: false,
    cardFormReady: false
  });

  useEffect(() => {
    if (flags.current.inited) return;
    flags.current.inited = true;
    let cancelled = false;

    const hidePageContent = () => {
      hide('c2p-loader');
      hide('clickToPaySignIn');
      hide('guestCheckoutPanel');
    };
    const showPageContent = () => {
      hide('c2p-popup');
      hide('c2p-loader');
      show('clickToPaySignIn');
      hide('guestCheckoutPanel');
      if (window.ClickToPay && window.ClickToPay.unbindCookies)
        window.ClickToPay.unbindCookies();
    };

    const confirmPay = async (correlationId, scheme) => {
      flags.current.settled = true;
      setPhase('paying');
      try {
        // Unified charge path: init_pay_web CLICK_TO_PAY registers the cart
        // (start3 mints the mco), charges via the C2P session, and records the
        // payment — the same pipeline as every other payment method. The
        // amount is computed server-side from the bag.
        const { data } = await initPayClickToPay({
          bag: bagRef.current,
          sessionId: sess.current.sessionId,
          correlationId: correlationId || '',
          scheme: scheme || ''
        });
        const parsed =
          typeof data === 'string'
            ? JSON.parse(data)
            : data && data.d
            ? JSON.parse(data.d)
            : data;
        if (parsed?.status === 'EXPIRED') {
          setErrorMsg(
            parsed.error_message || 'Items expired. Payment did not go through'
          );
          setPhase('error');
          return;
        }
        const mco = parsed?.Mastercard_order_id || '';
        sess.current.mco = mco;
        // Second-phase confirm: init_pay_web authorizes; confirm_c2p_web
        // finalizes the charge. The payment is not complete until this returns
        // OK, so a non-OK status or a thrown error blocks the receipt.
        const { data: confirmData } = await confirmClickToPay({
          mco,
          amount: sess.current.amount,
          sessionId: sess.current.sessionId,
          correlationId: correlationId || '',
          scheme: scheme || ''
        });
        // const confirmed =
        //   typeof confirmData === 'string'
        //     ? JSON.parse(confirmData)
        //     : confirmData && confirmData.d
        //     ? JSON.parse(confirmData.d)
        //     : confirmData;
        // if (confirmed?.status !== 'OK') {
        //   setErrorMsg(
        //     confirmed?.error_message ||
        //       'Payment could not be confirmed. Please try again.'
        //   );
        //   setPhase('error');
        //   return;
        // }
        // setPhase('done');
        if (onSuccess) onSuccess(mco);
      } catch (e) {
        setErrorMsg('Payment failedqqqqq');
        setPhase('error');
      }
    };

    // Hosted card fields (PaymentSession) are configured LAZILY, only when the
    // guest card form is actually visible on screen. MPGS session.js measures
    // each field element when configure() runs and attaches its secure iframe at
    // that size; if we configure while the fields are display:none they attach
    // at 0x0 and can never be typed into (this was the "can't type CVV" bug).
    // Also pass order.id and check the initialized status — both required by the
    // gateway for the hosted fields to come up. (Ported from click-to-pay-fixes.)
    const configurePaymentSession = () => {
      const CTP = window.ClickToPay;
      const PS = window.PaymentSession;
      if (!PS || flags.current.psConfigured) return;
      flags.current.psConfigured = true;
      try {
        // The mco does not exist yet (it is minted by start3 inside init_pay_web
        // at charge time), but the gateway needs an order id for the hosted
        // fields to attach. Use a placeholder: MPGS gives request parameters
        // precedence over session values, so the real mco passed as order.id in
        // the PAY request overrides this. VERIFY on the test merchant before
        // release — if PAY rejects the mismatch, fall back to pre-registering
        // (start_c2p_web) and skipping start3 in the backend when MCO is set.
        PS.configure({
          session: sess.current.sessionId,
          order: { id: 'C2P' + Date.now() },
          fields: {
            card: {
              number: '#card-number',
              securityCode: '#security-code',
              expiryMonth: '#expiry-month',
              expiryYear: '#expiry-year',
              nameOnCard: '#cardholder-name'
            }
          },
          frameEmbeddingMitigation: ['javascript'],
          callbacks: {
            initialized: (response) => {
              if (response && response.status === 'ok') {
                flags.current.cardFormReady = true;
              } else {
                const why =
                  (response && (response.message || response.status)) ||
                  'unknown';
                // Surface exactly why the hosted card fields failed to attach
                // instead of leaving a silently-dead field.
                console.error(
                  '[C2P] PaymentSession initialized NOT ok:',
                  why,
                  response
                );
                setErrorMsg('Could not load the card form: ' + why);
              }
            },
            formSessionUpdate: (response) => {
              if (response.status === 'ok' && flags.current.doEnroll) {
                flags.current.doEnroll = false;
                // The card is captured; the Click to Pay enrollment DCF is about
                // to render into #cardFacilitator. Hide every other panel now so
                // the DCF is the only thing on screen (otherwise the email panel
                // and the hosted-field form stay stacked behind it).
                hide('clickToPayEmailPanel');
                hide('clickToPaySignIn');
                hide('guestCheckoutPanel');
                hide('customNotMyCards');
                hide('dvAddingCard');
                hide('openGuestCheckoutButton');
                hide('c2p-popup');
                hide('c2p-loader');
                show('cardFacilitator');
                setTimeout(() => {
                  CTP.checkoutWithNewCard({
                    onComplete: (correlationId, scheme) =>
                      confirmPay(correlationId || '', scheme || ''),
                    onError: () => showPageContent(),
                    onCancel: () => showPageContent()
                  });
                  const observer = new MutationObserver((mutations, obs) => {
                    if (
                      document.querySelector(
                        "iframe[src*='src.mastercard.com']"
                      )
                    ) {
                      hidePageContent();
                      obs.disconnect();
                    }
                  });
                  observer.observe(document.body, {
                    childList: true,
                    subtree: true
                  });
                }, 100);
              } else if (response.status === 'ok' && !flags.current.doEnroll) {
                confirmPay('', '');
              }
            },
            onCancel: () => showPageContent(),
            onError: () => showPageContent()
          },
          interaction: {
            displayControl: {
              formatCard: 'EMBOSSED',
              invalidFieldCharacters: 'REJECT'
            }
          }
        });

        PS.onCardTypeChange((selector, result) => {
          if (result.status === 'SUPPORTED') {
            CTP.isEnrollmentAvailableForScheme(result.brand, (canEnroll) => {
              if (canEnroll && result.brand === 'MASTERCARD') {
                show('consent-containerMaster');
                hide('consent-containerVisa');
              } else if (canEnroll && result.brand === 'VISA') {
                show('consent-containerVisa');
                hide('consent-containerMaster');
              } else {
                hide('consent-containerVisa');
                hide('consent-containerMaster');
              }
            });
          }
        });
      } catch (e) {
        flags.current.psConfigured = false;
        console.error('[C2P] PaymentSession.configure threw', e);
        setErrorMsg('Could not load the card form. Please try again.');
      }
    };

    const configureSdk = () => {
      const CTP = window.ClickToPay;
      const PS = window.PaymentSession;
      if (!CTP || !PS) {
        setErrorMsg('Click to Pay SDK failed to load');
        setPhase('error');
        return;
      }

      CTP.configure({
        merchant: {
          id: sess.current.merchantId,
          name: 'Emirates Red Crescent',
          url: 'https://www.emiratesrc.ae/'
        },
        session: {
          id: sess.current.sessionId,
          wsVersion: sess.current.wsVersion
        },
        order: { amount: sess.current.amount, currency: 'AED' },
        interaction: { locale: 'ar_AE', country: 'ARE' },
        customer: {
          email:
            (document.getElementById('c2pEmailField') &&
              document.getElementById('c2pEmailField').value) ||
            undefined
        },
        elements: {
          cardList: 'cardListContainer',
          otp: 'otpContainer',
          dcf: 'cardFacilitator'
        },
        callbacks: {
          onComplete: (correlationId, scheme) =>
            confirmPay(correlationId, scheme),
          onStateChange: (changeInfo) => {
            const ns = changeInfo && changeInfo.newState;
            // When the Click to Pay DCF (card facilitator / enrollment) is on
            // screen it must be the ONLY panel. Hide everything else and return
            // early so the logic below (which re-shows the email panel whenever
            // the device is not recognized) can't stack panels behind the DCF.
            if (ns && ns.elementState?.dcf?.visible === true) {
              hide('clickToPayEmailPanel');
              hide('clickToPaySignIn');
              hide('guestCheckoutPanel');
              hide('customNotMyCards');
              hide('dvAddingCard');
              hide('openGuestCheckoutButton');
              hide('c2p-popup');
              hide('c2p-loader');
              return;
            }
            if (
              ns &&
              (ns.elementState?.cardList?.visible === true ||
                ns.elementState?.otp?.visible === true ||
                ns.elementState?.dcf?.visible === true)
            ) {
              hide('c2p-loader');
            }
            if (
              ns &&
              (ns.payerState?.deviceRecognized === true ||
                ns.payerState?.emailEnrolled === true)
            ) {
              setTimeout(() => {
                hide('clickToPayEmailPanel');
                hide('clickToPaySignIn');
              }, 100);
            } else {
              show('clickToPayEmailPanel');
              if (flags.current.emailLookupInitiated) {
                hide('c2p-loader');
                show('c2p-popup');
              }
            }
            if (changeInfo?.diff?.elementState?.dcf?.visible === false) {
              if (changeInfo?.diff?.elementState?.cardList?.visible !== true)
                showPageContent();
            }
            if (changeInfo?.diff?.customerState?.emailEnrolled === false)
              showPageContent();
            const guest = document.getElementById('guestCheckoutPanel');
            if (
              ns?.elementState?.cardList?.visible === true &&
              ns?.elementState?.otp?.visible === false &&
              guest &&
              getComputedStyle(guest).display === 'none'
            ) {
              const el = document.getElementById('maskedEmail');
              if (el) el.textContent = maskEmail(ns?.customerState?.email);
              show('customNotMyCards');
              show('dvAddingCard');
            } else {
              hide('dvAddingCard');
              hide('customNotMyCards');
            }
          },
          onError: (errInfo) => {
            const msg = String(
              (errInfo && (errInfo.errorMessage || errInfo.message)) || ''
            );
            const fatal =
              /configure\(\) did not complete/i.test(msg) ||
              /populateAcceptedShipping/i.test(msg) ||
              /Cannot read properties of undefined/i.test(msg);
            if (!flags.current.settled && fatal) {
              flags.current.settled = true;
              setErrorMsg(
                'Click to Pay is not available right now. Please use the "Add Credit/Debit Card" option instead.'
              );
              setPhase('error');
            }
          },
          onCancel: () => showPageContent()
        }
      });
    };

    const setup = async () => {
      try {
        const bag = buildPaymentBag({ bag: cart });
        // Guard against an empty cart (e.g. opened before the bag finished
        // loading) — an empty donation list has nothing to register or charge.
        // Show a friendly message instead.
        if (!bag || bag.length === 0) {
          setErrorMsg(
            'Your cart is still loading. Please close and try again.'
          );
          setPhase('error');
          return;
        }
        bagRef.current = bag;
        // Session only — the cart is NOT registered here anymore. init_pay_web
        // registers it at charge time (confirmPay), like every other payment
        // method, so an abandoned modal no longer leaves orphan donation rows.
        const res = await createC2PSession();
        const start =
          typeof res.data === 'string'
            ? JSON.parse(res.data)
            : res.data && res.data.d
            ? JSON.parse(res.data.d)
            : res.data;
        if (!start || start.status !== 'OK') {
          // Log the raw backend detail for debugging, but never show the raw
          // 500/stack trace to the shopper.
          if (start && start.error)
            console.error('[C2P] start error', start.error);
          setErrorMsg('Could not start Click to Pay. Please try again.');
          setPhase('error');
          return;
        }
        if (cancelled) return;
        sess.current = {
          mco: '',
          amount: String(computeBagTotal(bag)),
          sessionId: start.sessionId,
          merchantId: start.merchantId,
          wsVersion: start.wsVersion || '100'
        };
        const hd = document.getElementById('hdSessionID');
        if (hd) hd.value = start.sessionId;
        const host = (start.gatewayHost || '').replace(/\/+$/, '');
        await loadScript(
          `${host}/form/version/${sess.current.wsVersion}/merchant/${start.merchantId}/session.js`
        );
        await loadScript(`${host}/static/click-to-pay/click-to-pay.min.js`);
        if (cancelled) return;
        configureSdk();
        setPhase('ready');
      } catch (e) {
        setErrorMsg(e.message || 'Click to Pay error');
        setPhase('error');
      }
    };

    // expose the handlers the JSX buttons call
    window.__erc_c2p = {
      lookupEmail: () => {
        const field = document.getElementById('c2pEmailField');
        const email = (field && field.value) || '';
        if (!email) {
          toastifyMessage('Please enter your email', undefined, 'warning');
          return;
        }
        hide('clickToPayEmailPanel');
        show('c2p-loader');
        flags.current.emailLookupInitiated = true;
        if (window.ClickToPay) window.ClickToPay.lookupCustomer(email);
      },
      openGuest: () => {
        // Reference showHideDivs(): just reveal the hosted-field guest form.
        // The card the user types is pushed into the session by
        // PaymentSession.updateSessionFromForm() in pay(). Click to Pay
        // enrollment (checkoutWithNewCard) only runs later, inside the
        // formSessionUpdate callback, and only if the consent box is ticked.
        // Calling checkoutWithNewCard() here — on a session with no source of
        // funds — is what caused the POST /session/{id} 500.
        hide('clickToPayEmailPanel');
        hide('c2p-loader');
        hide('c2p-popup');
        show('guestCheckoutPanel');
        hide('openGuestCheckoutButton');
        // Configure the hosted fields now that the form is visible, so MPGS
        // sizes its secure iframes correctly and the fields are typable.
        configurePaymentSession();
      },
      closeGuest: () => {
        if (flags.current.addingNewCard) {
          show('cardListContainer');
          show('dvAddingCard');
        } else {
          hide('c2p-loader');
          show('clickToPaySignIn');
        }
        hide('guestCheckoutPanel');
        show('openGuestCheckoutButton');
      },
      addNewCard: () => {
        // Reference addNewCard(): show the guest form, hide the card list.
        show('guestCheckoutPanel');
        hide('openGuestCheckoutButton');
        hide('dvAddingCard');
        hide('cardListContainer');
        flags.current.addingNewCard = true;
        // Configure the hosted fields now that the form is visible.
        configurePaymentSession();
      },
      notMyCards: () => {
        hide('customNotMyCards');
        hide('dvAddingCard');
        hide('cardListContainer');
        hide('c2p-loader');
        show('clickToPaySignIn');
        show('clickToPayEmailPanel');
        const ef = document.getElementById('c2pEmailField');
        if (ef) ef.value = '';
        if (window.ClickToPay) window.ClickToPay.unbindCookies();
      },
      pay: () => {
        flags.current.doEnroll = checkC2PConsent();
        if (window.PaymentSession)
          window.PaymentSession.updateSessionFromForm('card');
        hide('guestCheckoutPanel');
        return false;
      }
    };

    setup();
    return () => {
      cancelled = true;
      delete window.__erc_c2p;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="c2p-overlay">
      <div className="c2p-modal">
        <button
          type="button"
          className="c2p-close"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>

        {phase === 'loading' && (
          <div className="c2p-status">
            Preparing Click to Pay…
            <div className="c2p-spinner" />
          </div>
        )}

        {phase === 'paying' && (
          <div className="c2p-status">
            Processing your payment…
            <div className="c2p-spinner" />
          </div>
        )}

        {phase === 'done' && (
          <div className="c2p-status c2p-thanks">
            Thank you. Your donation was completed.
            <div className="c2p-ref">Reference: {sess.current.mco}</div>
          </div>
        )}

        {phase === 'error' && (
          <div className="c2p-status c2p-error">
            {errorMsg || 'Payment could not be completed. Please try again.'}
          </div>
        )}

        <div
          className="c2p-body"
          style={{ display: phase === 'ready' ? 'block' : 'none' }}
        >
          <input type="hidden" id="hdSessionID" defaultValue="" />

          <div className="dv-images">
            <src-button
              locale=""
              theme="light"
              card-brands='["mastercard","visa"]'
            ></src-button>
          </div>

          <h5 id="c2p-msg">Complete payment with Click to Pay</h5>

          <div
            id="customNotMyCards"
            className="c2p-notCard"
            style={{ display: 'none' }}
          >
            <label id="maskedEmail">a****g@mail.com</label>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                window.__erc_c2p?.notMyCards();
              }}
            >
              Not your cards?
            </a>
          </div>

          <div id="cardListContainer"></div>
          <div id="otpContainer"></div>
          <div id="cardFacilitator"></div>

          <div id="dvAddingCard" style={{ display: 'none' }}>
            <div className="c2p-or">Or</div>
            <button
              id="addNewCardBtn"
              type="button"
              onClick={() => window.__erc_c2p?.addNewCard()}
            >
              Add New Card
            </button>
          </div>

          <div id="c2p-loader" style={{ display: 'none' }}>
            <src-loader locale=""></src-loader>
            <div>Processing…</div>
          </div>

          <div id="c2p-popup" className="c2p-popup" style={{ display: 'none' }}>
            <div className="c2p-popup-content">
              <span>
                We could not find a Click to Pay account linked with this email.
                Proceed with manual card entry.
              </span>
              <button
                type="button"
                onClick={() => window.__erc_c2p?.openGuest()}
              >
                OK
              </button>
            </div>
          </div>

          <div id="clickToPaySignIn">
            <div id="clickToPayEmailPanel">
              <label htmlFor="c2pEmailField">Email / البريد الالكتروني</label>
              <input
                id="c2pEmailField"
                type="email"
                maxLength={254}
                defaultValue=""
              />
              <button
                type="button"
                onClick={() => window.__erc_c2p?.lookupEmail()}
              >
                Continue
              </button>
            </div>
          </div>

          <div id="guestCheckoutPanel" style={{ display: 'none' }}>
            <button
              type="button"
              className="c2p-close"
              onClick={() => window.__erc_c2p?.closeGuest()}
            >
              ×
            </button>
            <h5>Enter the card details</h5>

            <label htmlFor="card-number">Card Number / رقم البطاقة</label>
            <input
              id="card-number"
              type="number"
              inputMode="numeric"
              dir="ltr"
              className="c2p-hosted-field"
              required
              readOnly
            />
            <label htmlFor="expiry-month">Expiry date / تاريخ الإنتهاء</label>
            <div className="c2p-expiry">
              <input
                id="expiry-month"
                type="text"
                inputMode="numeric"
                placeholder="MM"
                maxLength={2}
                dir="ltr"
                className="c2p-hosted-field"
                required
                readOnly
              />
              <input
                id="expiry-year"
                type="text"
                inputMode="numeric"
                placeholder="YY"
                maxLength={2}
                dir="ltr"
                className="c2p-hosted-field"
                required
                readOnly
              />
            </div>
            <label htmlFor="security-code">CVC / الرقم التعريفي</label>
            <input
              id="security-code"
              type="text"
              inputMode="numeric"
              className="c2p-hosted-field"
              required
              readOnly
            />
            <label htmlFor="cardholder-name">
              Cardholder Name / اسم حامل البطاقة
            </label>
            <input
              id="cardholder-name"
              type="text"
              className="c2p-hosted-field"
              required
              readOnly
            />

            <div id="consent-containerMaster" style={{ display: 'none' }}>
              <src-consent
                locale="en_US"
                card-brands="mastercard"
                display-remember-me="false"
                dcf-suppressed="false"
              ></src-consent>
            </div>
            <div id="consent-containerVisa" style={{ display: 'none' }}>
              <src-consent
                locale="en_US"
                card-brands="visa"
                display-remember-me="false"
                dcf-suppressed="false"
              ></src-consent>
            </div>

            <button
              type="button"
              className="c2p-pay-btn"
              onClick={() => window.__erc_c2p?.pay()}
            >
              Pay - أدفع
            </button>
          </div>

          <a
            href="#"
            id="openGuestCheckoutButton"
            onClick={(e) => {
              e.preventDefault();
              window.__erc_c2p?.openGuest();
            }}
          >
            Write card details
          </a>
        </div>
      </div>
    </div>
  );
};

export default ClickToPayCheckout;
